import { FiscalEventInterface, TypeOptions } from '@app/interface/fiscal-event';
import { format, parseISO, addDays } from 'date-fns';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FiscalEventApi } from '@app/services/fiscal-event-api.service';
import { NgForm } from '@angular/forms';
import { Inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { ToastService } from '@app/services/toast.service';

@Component({
  selector: 'izy-new-fiscal-event-dialog',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSelectModule,
    MatOptionModule,
    CommonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  templateUrl: './new-fiscal-event-dialog.component.html',
  styleUrl: './new-fiscal-event-dialog.component.scss',
})
export class NewFiscalEventDialogComponent {
  private toast = inject(ToastService);
  typeOptions: TypeOptions[] = [
    { code: 'TASSE', name: 'Tasse' },
    { code: 'CONTRIBUTI', name: 'Contributi' },
  ];

  newEvent: Partial<FiscalEventInterface> = {
    id: 1,
    name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'TASSE',
    description: '',
    amount: 0,

    is_paid: true,

    organization_id: 0,
    // organization: 0,
  };

  constructor(
    private fiscalEventService: FiscalEventApi,
    private router: Router,
    private route: ActivatedRoute,
    public dialogRef: MatDialogRef<NewFiscalEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { organizationId: number },
  ) {}

  addNewEvent(form?: NgForm) {
    const organization_id = this.data.organizationId; // Use the passed organization_id

    this.newEvent.organization_id = organization_id;
    // this.newEvent.organization = organization_id;
    this.newEvent.is_created_by_user = true;

    // Remove id from newEvent as it should be generated by the backend
    const eventToCreate = { ...this.newEvent };
    delete eventToCreate.id;

    // Format the date to YYYY-MM-DD
    const formattedEvent = {
      ...this.newEvent,
      date: this.formatDateForAPI(this.newEvent.date),
    };

    this.fiscalEventService
      .createFiscalEvent(organization_id, formattedEvent)
      .subscribe({
        next: (createdEvent: FiscalEventInterface) => {
          this.showSuccessMessage(
            `Evento ${createdEvent.name} aggiunto con successo!`,
          );
          console.log(createdEvent);

          // Close the dialog and return success
          this.dialogRef.close('success');
        },
        error: (err: any) => {
          console.error('Error creating fiscal event:', err);
          this.showErrorMessage("Errore nella creazione dell'evento fiscale");
        },
      });
  }

  // Helper method to format date as YYYY-MM-DD
  private formatDateForAPI(date: any): string {
    if (!date) return '';

    // If it's already a string in the correct format, return it
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // If it's a Date object or string that needs formatting
    const dateObj = new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', date);
      return '';
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  backToTable(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/fiscal_plan`]);
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}
